import angular from 'angular';
import '../../layouts.module';
import template from './topbar.component.html';
import './topbar.component.scss';
import { APP_VERSION } from '../../../core/constants/api.constant';
import { REALTIME_EVENT } from '../../../core/services/realtime.service';

const POLL_INTERVAL_MS = 30000;

class TopbarController {
  constructor(
    ThemeService,
    SessionService,
    AuthService,
    NotificationsApiService,
    $state,
    $interval,
    RealtimeService,
    EventBusService,
    $window,
    $http,
    API_BASE_URL
  ) {
    this.version = APP_VERSION;
    this.RealtimeService = RealtimeService;
    this.EventBusService = EventBusService;
    this.$window = $window;
    this.$http = $http;
    this.API_BASE_URL = API_BASE_URL;
    this.ThemeService = ThemeService;
    this.SessionService = SessionService;
    this.AuthService = AuthService;
    this.NotificationsApiService = NotificationsApiService;
    this.$state = $state;
    this.$interval = $interval;
    this.userMenuOpen = false;
    this.notificationsOpen = false;
    this.notifications = [];
    this.unreadCount = 0;
  }

  $onInit() {
    this.user = this.SessionService.user;
    this.theme = this.ThemeService.current;
    this.themeSubscription = this.ThemeService.theme$.subscribe((theme) => {
      this.theme = theme;
    });

    const preferences = (this.user && this.user.preferences) || {};
    this.browserNotifications =
      Boolean(preferences.browserNotifications) && this.browserPermission === 'granted';
    this.refreshUnreadCount(true);
    this.pollPromise = this.$interval(() => this.refreshUnreadCount(), POLL_INTERVAL_MS);
    this.realtimeSubscription = this.EventBusService.on(REALTIME_EVENT).subscribe((event) => {
      if (event && event.type === 'notification.created') this.onNewNotification();
    });
    this.realtimeConnected = this.SessionService.hasPermission('conversation.read');
    if (this.realtimeConnected) this.RealtimeService.connect();
  }

  get browserSupported() {
    return Boolean(this.$window.Notification);
  }

  get browserPermission() {
    return this.browserSupported ? this.$window.Notification.permission : 'unsupported';
  }

  refreshUnreadCount(initial = false) {
    return this.NotificationsApiService.unreadCount().then((count) => {
      const grew = !initial && count > this.unreadCount;
      this.unreadCount = count;
      if (grew) this.showBrowserNotification();
      return count;
    });
  }

  onNewNotification() {
    this.refreshUnreadCount();
    if (this.notificationsOpen) {
      this.NotificationsApiService.list().then((notifications) => {
        this.notifications = notifications;
      });
    }
  }

  showBrowserNotification() {
    if (!this.browserNotifications || this.browserPermission !== 'granted') return;
    this.NotificationsApiService.list().then((notifications) => {
      const latest = notifications.find((notification) => !notification.read);
      if (!latest) return;
      const native = new this.$window.Notification(latest.title || 'CRM CashPago', {
        body: latest.text,
        tag: latest.id
      });
      native.onclick = () => {
        this.$window.focus();
        this.openNotification(latest);
      };
    });
  }

  toggleBrowserNotifications() {
    if (this.browserNotifications) {
      this.browserNotifications = false;
      this.saveBrowserPreference(false);
      return;
    }
    if (!this.browserSupported) return;
    const ask =
      this.browserPermission === 'default'
        ? this.$window.Notification.requestPermission()
        : Promise.resolve(this.browserPermission);
    ask.then((permission) => {
      this.browserNotifications = permission === 'granted';
      if (this.browserNotifications) this.saveBrowserPreference(true);
    });
  }

  saveBrowserPreference(enabled) {
    if (this.user)
      this.user.preferences = { ...(this.user.preferences || {}), browserNotifications: enabled };
    return this.$http
      .patch(
        `${this.API_BASE_URL}/users/me/preferences`,
        { browserNotifications: enabled },
        { silentErrors: true }
      )
      .catch(() => null);
  }

  openAllNotifications() {
    this.closeNotifications();
    this.$state.go('app.notifications');
  }

  toggleNotifications() {
    this.notificationsOpen = !this.notificationsOpen;
    if (this.notificationsOpen) {
      this.NotificationsApiService.list().then((notifications) => {
        this.notifications = notifications;
      });
    }
  }

  closeNotifications() {
    this.notificationsOpen = false;
  }

  openNotification(notification) {
    if (!notification.read) {
      this.NotificationsApiService.markRead(notification.id).then(() => {
        notification.read = true;
        this.refreshUnreadCount();
      });
    }
    this.closeNotifications();
    const target = notification.target;
    if (target && this.isAvailable(target.state)) {
      this.$state.go(target.state, target.params || {}, { reload: target.state });
    }
  }

  markAllRead() {
    return this.NotificationsApiService.markAllRead().then(() => {
      this.notifications.forEach((notification) => {
        notification.read = true;
      });
      this.unreadCount = 0;
    });
  }

  toggleTheme() {
    this.ThemeService.toggle();
  }

  toggleUserMenu() {
    this.userMenuOpen = !this.userMenuOpen;
  }

  closeUserMenu() {
    this.userMenuOpen = false;
  }

  isAvailable(state) {
    return Boolean(this.$state.get(state) || this.$state.get(`${state}.**`));
  }

  logout() {
    this.AuthService.logout();
    this.$state.go('auth.login');
  }

  $onDestroy() {
    if (this.themeSubscription) this.themeSubscription.unsubscribe();
    if (this.pollPromise) this.$interval.cancel(this.pollPromise);
    if (this.realtimeSubscription) this.realtimeSubscription.unsubscribe();
    if (this.realtimeConnected) this.RealtimeService.disconnect();
  }
}

TopbarController.$inject = [
  'ThemeService',
  'SessionService',
  'AuthService',
  'NotificationsApiService',
  '$state',
  '$interval',
  'RealtimeService',
  'EventBusService',
  '$window',
  '$http',
  'API_BASE_URL'
];

angular.module('app.layouts').component('cpTopbar', {
  controller: TopbarController,
  controllerAs: 'vm',
  bindings: {
    menuOpen: '<',
    onMenu: '&'
  },
  template
});
